import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsBc, useBonCommande, useBudgetLignes, useRequisition, useStructures, type BcSaisie } from '@/api/hooks';
import { LIBELLE_CATEGORIE_ACHAT, type BonCommande, type CategorieAchat } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, PageHeader } from '@/components/common';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ActiviteSousActivite } from '@/components/documents';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Textarea } from '@/components/ui/textarea';
import { montant } from '@/lib/utils';

const TAUX_DEFAUT: Record<string, number> = { USD: 1, CDF: 1 / 2850, EUR: 1.08 };

interface Valeurs {
  objet: string;
  structureCode: string;
  ligneBudgetaireId: string;
  requisition: string;
  categorie: CategorieAchat;
  fournisseur: string;
  lignes: Array<{ designation: string; quantite: number; unite: string; prixUnitaire: number }>;
  consultations: Array<{ fournisseur: string; montant: number }>;
  justification: string;
  delaiLivraison: string;
  lieuLivraison: string;
  devise: 'USD' | 'CDF' | 'EUR';
  tauxChange: number;
}

const ligneVide = () => ({ designation: '', quantite: 1, unite: 'pièce', prixUnitaire: 0 });

const depuisBc = (b: BonCommande): Valeurs => ({
  objet: b.objet,
  structureCode: b.structure?.code ?? '',
  ligneBudgetaireId: b.ligneBudgetaireId,
  requisition: b.requisition ?? '',
  categorie: b.categorie,
  fournisseur: b.fournisseur,
  lignes: b.lignes,
  consultations: b.consultations.filter((c) => c.fournisseur.toLowerCase() !== b.fournisseur.toLowerCase()),
  justification: b.justification ?? '',
  delaiLivraison: b.delaiLivraison ?? '',
  lieuLivraison: b.lieuLivraison ?? '',
  devise: (b.devise as Valeurs['devise']) ?? 'USD',
  tauxChange: b.montant > 0 ? Math.round((b.montantUsd / b.montant) * 1_000_000) / 1_000_000 : TAUX_DEFAUT[b.devise] ?? 1,
});

const versSaisie = (v: Valeurs): BcSaisie => ({
  objet: v.objet.trim(),
  structureCode: v.structureCode || undefined,
  ligneBudgetaireId: v.ligneBudgetaireId,
  requisition: v.requisition.trim() || undefined,
  categorie: v.categorie,
  fournisseur: v.fournisseur.trim(),
  lignes: v.lignes.filter((l) => l.designation.trim()).map((l) => ({ designation: l.designation.trim(), quantite: Number(l.quantite) || 0, unite: l.unite.trim() || 'pièce', prixUnitaire: Number(l.prixUnitaire) || 0 })),
  consultations: v.consultations.filter((c) => c.fournisseur.trim()).map((c) => ({ fournisseur: c.fournisseur.trim(), montant: Number(c.montant) || 0 })),
  justification: v.justification.trim() || undefined,
  delaiLivraison: v.delaiLivraison.trim() || undefined,
  lieuLivraison: v.lieuLivraison.trim() || undefined,
  devise: v.devise,
  tauxChange: v.devise === 'USD' ? undefined : Number(v.tauxChange) || TAUX_DEFAUT[v.devise],
});

/** Bon de commande : nouveau ou en correction (émis par les achats). */
export function BcFormulaire() {
  const { id } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const u = session!.utilisateur;
  const existant = useBonCommande(id);
  const structures = useStructures();
  const actions = useActionsBc();
  // Transformation d'une réquisition autorisée (v1.1, § 10) : « /achats/nouveau?requisition=<id> » préremplit le bon.
  const [params] = useSearchParams();
  const requisitionId = id ? undefined : (params.get('requisition') ?? undefined);
  const requisition = useRequisition(requisitionId);
  const form = useForm<Valeurs>({
    defaultValues: { objet: '', structureCode: u.structureCode, ligneBudgetaireId: '', requisition: '', categorie: 'FOURNITURES', fournisseur: '', lignes: [ligneVide()], consultations: [{ fournisseur: '', montant: 0 }], justification: '', delaiLivraison: '', lieuLivraison: '', devise: 'USD', tauxChange: 1 },
  });
  const lignes = useFieldArray({ control: form.control, name: 'lignes' });
  const consultations = useFieldArray({ control: form.control, name: 'consultations' });
  const valeurs = useWatch({ control: form.control });
  const [toutesLignes, setToutesLignes] = useState(Boolean(requisitionId));
  const budget = useBudgetLignes(toutesLignes ? undefined : valeurs.structureCode || u.structureCode);

  useEffect(() => {
    if (existant.data) form.reset(depuisBc(existant.data.bonCommande));
  }, [existant.data, form]);

  useEffect(() => {
    if (!requisition.data || id) return;
    const r = requisition.data.requisition;
    form.reset({
      ...form.getValues(),
      objet: r.objet,
      structureCode: r.structure?.code ?? u.structureCode,
      ligneBudgetaireId: r.ligneBudgetaireId,
      requisition: r.numero ?? '',
      lignes: r.articles.map((a) => ({ designation: a.designation, quantite: a.quantite, unite: a.unite, prixUnitaire: a.estimationUsd })),
    });
  }, [requisition.data, id, form, u.structureCode]);

  // La liste des structures arrive après le rendu : la valeur par défaut est réappliquée au champ natif.
  useEffect(() => {
    if (structures.data?.length && !id) form.setValue('structureCode', form.getValues('structureCode') || u.structureCode);
  }, [structures.data, id, form, u.structureCode]);

  if (id && existant.isLoading) return <Chargement />;
  const devise = valeurs.devise ?? 'USD';
  const taux = devise === 'USD' ? 1 : Number(valeurs.tauxChange) || TAUX_DEFAUT[devise] || 1;
  const total = (valeurs.lignes ?? []).reduce((s, l) => s + (Number(l?.quantite) || 0) * (Number(l?.prixUnitaire) || 0), 0);
  const offres = [{ fournisseur: valeurs.fournisseur ?? '', montant: total }, ...(valeurs.consultations ?? []).filter((c) => c?.fournisseur?.trim()).map((c) => ({ fournisseur: c!.fournisseur!, montant: Number(c!.montant) || 0 }))];
  const moinsDisant = offres.reduce((m, c) => (c.montant < m.montant ? c : m), offres[0]!);
  const retenuPlusCher = offres.length > 1 && moinsDisant.fournisseur.toLowerCase() !== (valeurs.fournisseur ?? '').toLowerCase();

  const enregistrer = form.handleSubmit(
    async (v) => {
      const corps: BcSaisie = { ...versSaisie(v), ...(requisitionId ? { requisitionId } : {}) };
      if (!corps.lignes.length) return toast.error('Ajoutez au moins une ligne.');
      try {
        const r = id ? await actions.modifier.mutateAsync({ id, corps }) : await actions.creer.mutateAsync(corps);
        toast.success(id ? 'Bon de commande modifié.' : 'Bon de commande enregistré en brouillon.');
        navigate(`/achats/${r.bonCommande.id}`);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
      }
    },
    () => toast.error("Renseignez l'objet, la ligne budgétaire, le fournisseur et les lignes."),
  );

  return (
    <form onSubmit={enregistrer} className="space-y-6">
      <PageHeader
        retour={
          <Link to={id ? `/achats/${id}` : '/achats'} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Retour
          </Link>
        }
        titre={id ? `Corriger le bon de commande ${existant.data?.bonCommande.numero ?? ''}` : requisition.data ? `Bon de commande pour la réquisition ${requisition.data.requisition.numero ?? ''}` : 'Nouveau bon de commande'}
        sousTitre={`Émis par ${u.prenom} ${u.nom}. ${requisition.data ? `Articles et estimations repris de la réquisition (${montant(requisition.data.requisition.montantEstime, true)}) : fixez les prix après consultation. ` : ''}Le fournisseur retenu doit être le moins-disant des offres consultées, sinon une justification est requise.`}
        actions={
          <Button type="submit" disabled={actions.creer.isPending || actions.modifier.isPending}>
            {id ? 'Enregistrer les corrections' : 'Enregistrer le brouillon'}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Commande</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="objet">Objet</Label>
            <Input id="objet" {...form.register('objet', { required: true, minLength: 5 })} placeholder="Billets d'avion pour la mission de Bunia" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="structure">Structure demandeuse</Label>
            <SelectNatif id="structure" {...form.register('structureCode')}>
              {(structures.data ?? []).map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} · {s.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <ActiviteSousActivite lignes={budget.data ?? []} ligneId={valeurs.ligneBudgetaireId ?? ''} setLigneId={(id) => form.setValue('ligneBudgetaireId', id, { shouldDirty: true, shouldValidate: true })} />
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox checked={toutesLignes} onCheckedChange={(c) => setToutesLignes(c === true)} />
              Afficher toutes les lignes du Fonds
            </label>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="requisition">Réquisition</Label>
            <Input id="requisition" {...form.register('requisition')} placeholder="REQ/001/10/2026" readOnly={Boolean(requisitionId)} />
            {requisitionId && (
              <p className="text-xs text-muted-foreground">
                Rattaché à la{' '}
                <Link to={`/requisitions/${requisitionId}`} className="text-primary hover:underline">
                  réquisition autorisée
                </Link>
                .
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="categorie">Catégorie</Label>
            <SelectNatif id="categorie" {...form.register('categorie')}>
              {(Object.keys(LIBELLE_CATEGORIE_ACHAT) as CategorieAchat[]).map((c) => (
                <option key={c} value={c}>
                  {LIBELLE_CATEGORIE_ACHAT[c]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fournisseur">Fournisseur retenu</Label>
            <Input id="fournisseur" {...form.register('fournisseur', { required: true })} placeholder="Voyages Horizon SARL" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="devise">Devise</Label>
              <SelectNatif id="devise" {...form.register('devise')}>
                <option value="USD">USD</option>
                <option value="CDF">CDF</option>
                <option value="EUR">EUR</option>
              </SelectNatif>
            </div>
            {devise !== 'USD' && (
              <div className="space-y-1.5">
                <Label htmlFor="taux">1 {devise} en USD</Label>
                <Input id="taux" type="number" step="0.000001" min={0} {...form.register('tauxChange', { valueAsNumber: true })} />
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="delai">Délai de livraison</Label>
            <Input id="delai" {...form.register('delaiLivraison')} placeholder="Émission des billets sous 48 heures" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lieu">Lieu de livraison</Label>
            <Input id="lieu" {...form.register('lieuLivraison')} placeholder="Siège du FONAREV, Kinshasa" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Lignes <span className="num float-right">{montant(total, true)}{devise !== 'USD' ? ` ${devise} · ${montant(total * taux, true)}` : ''}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {lignes.fields.map((f, i) => (
            <div key={f.id} className="grid gap-2 md:grid-cols-[1fr_6rem_7rem_8rem_auto]">
              <Input placeholder="Désignation" {...form.register(`lignes.${i}.designation`)} />
              <Input className="text-right" type="number" min={0} step="0.01" placeholder="Qté" {...form.register(`lignes.${i}.quantite`, { valueAsNumber: true })} />
              <Input placeholder="Unité" {...form.register(`lignes.${i}.unite`)} />
              <Input className="text-right" type="number" min={0} step="0.01" placeholder="Prix unitaire" {...form.register(`lignes.${i}.prixUnitaire`, { valueAsNumber: true })} />
              <Button type="button" variant="ghost" size="icon" onClick={() => lignes.remove(i)} aria-label="Retirer" disabled={lignes.fields.length === 1}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => lignes.append(ligneVide())}>
            <Plus className="size-4" /> Ajouter une ligne
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Offres consultées</CardTitle>
          <CardDescription>Autres fournisseurs consultés et montant total de leur offre. Le fournisseur retenu figure automatiquement avec le montant du bon.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {consultations.fields.map((f, i) => (
            <div key={f.id} className="grid gap-2 md:grid-cols-[1fr_10rem_auto]">
              <Input placeholder="Fournisseur consulté" {...form.register(`consultations.${i}.fournisseur`)} />
              <Input className="text-right" type="number" min={0} step="0.01" placeholder={`Montant (${devise})`} {...form.register(`consultations.${i}.montant`, { valueAsNumber: true })} />
              <Button type="button" variant="ghost" size="icon" onClick={() => consultations.remove(i)} aria-label="Retirer">
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => consultations.append({ fournisseur: '', montant: 0 })}>
            <Plus className="size-4" /> Ajouter une offre
          </Button>
          {retenuPlusCher && (
            <Alert className="mt-3">
              <AlertTitle>Le fournisseur retenu n'est pas le moins-disant</AlertTitle>
              <AlertDescription>
                Offre la plus basse : {moinsDisant.fournisseur} à {montant(moinsDisant.montant, true)}. Une justification (20 caractères au moins) est obligatoire pour soumettre.
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5 pt-2">
            <Label htmlFor="justif">Justification du choix {retenuPlusCher ? '(obligatoire)' : '(facultative)'}</Label>
            <Textarea id="justif" rows={3} {...form.register('justification')} placeholder="Délai, qualité, exclusivité, disponibilité…" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" asChild>
          <Link to={id ? `/achats/${id}` : '/achats'}>Abandonner</Link>
        </Button>
        <Button type="submit" disabled={actions.creer.isPending || actions.modifier.isPending}>
          {id ? 'Enregistrer les corrections' : 'Enregistrer le brouillon'}
        </Button>
      </div>
    </form>
  );
}
